import {
  prisma,
  reportingPrisma,
  type Prisma,
  DocumentEntityType,
} from "client";
import moment from "moment-timezone";
import {
  ParticipantGroupType,
  EntityModel,
  type Profile,
  type User,
} from "client";
import {
  addParticipantsToParticipantGroup,
  getParticipantGroupsByEventIdAndType,
} from "../participantGroup";
import { getEvent } from "../event";
import { getBookmarkedEntityIds } from "../bookmark";
import { getProfileAgendaSessionIds } from "../profile-agenda";
import { getDayUtcRange } from "../../utils/dayRange";
import { getProfileByEventIdAndUserId } from "../profile";
import {
  buildSessionCsvExportContent,
  buildSessionCsvTemplate,
} from "./csv-template";
import { expandFacetMatches, trimFacetValue } from "./facet-match";
import {
  collectSpeakerUserIds,
  filterSessionsForPublicSearch,
  normalizePublicSessionPagination,
  normalizeSessionSearchQuery,
  pageSessionRows,
  PUBLIC_SESSIONS_V2_LIST_MAX_PAGE_SIZE,
  PUBLIC_SESSIONS_V2_MAX_PAGE_SIZE,
  trimFacetList,
  type SessionSearchRow,
} from "./session-search.pure";

export type SessionV2TranslationInput = {
  languageId: string;
  languageCode?: string;
  name: string;
  description?: string;
  location?: string;
  track?: string;
  type?: string;
  topics?: string[];
};

const DEFAULT_SESSION_LANGUAGE = "en";

function normalizeTopics(topics?: string[] | null): string[] {
  if (!topics?.length) return [];
  return topics.map((topic) => topic.trim()).filter(Boolean);
}

function defaultTranslationIndex(
  translations: SessionV2TranslationInput[]
): number {
  const englishIndex = translations.findIndex(
    (translation) => translation.languageCode === DEFAULT_SESSION_LANGUAGE
  );
  return englishIndex >= 0 ? englishIndex : 0;
}

async function withLanguageCodes(
  translations: SessionV2TranslationInput[]
): Promise<SessionV2TranslationInput[]> {
  const missingIds = [
    ...new Set(
      translations
        .filter((translation) => !translation.languageCode)
        .map((translation) => translation.languageId)
    ),
  ];
  if (!missingIds.length) return translations;

  const languages = await prisma.language.findMany({
    where: { id: { in: missingIds } },
    select: { id: true, code: true },
  });
  const codeById = new Map(
    languages.map((language) => [language.id, language.code])
  );

  return translations.map((translation) => ({
    ...translation,
    languageCode: translation.languageCode ?? codeById.get(translation.languageId),
  }));
}

function mergeLegacyTopics(
  translations: SessionV2TranslationInput[],
  legacyTopics?: string[]
): SessionV2TranslationInput[] {
  const normalizedLegacy = normalizeTopics(legacyTopics);
  const defaultIndex = defaultTranslationIndex(translations);
  return translations.map((translation, index) => {
    const own = normalizeTopics(translation.topics);
    if (own.length) return { ...translation, topics: own };
    if (index === defaultIndex && normalizedLegacy.length) {
      return { ...translation, topics: normalizedLegacy };
    }
    return { ...translation, topics: own };
  });
}

function defaultLanguageTopics(
  translations: SessionV2TranslationInput[]
): string[] {
  return normalizeTopics(
    translations[defaultTranslationIndex(translations)]?.topics
  );
}

function translationWriteData(translation: SessionV2TranslationInput) {
  return {
    languageId: translation.languageId,
    name: translation.name,
    description: translation.description ?? null,
    location: trimFacetValue(translation.location) ?? null,
    track: trimFacetValue(translation.track) ?? null,
    type: trimFacetValue(translation.type) ?? null,
    topics: normalizeTopics(translation.topics),
  };
}

type SessionTopicSource = {
  topics?: string[] | null;
  translations?: Array<{
    topics?: string[] | null;
    language?: { code?: string | null } | null;
  }>;
};

/** Locale topics first; default-language (en, else first) next; then deprecated session.topics. */
function resolveSessionTopics(session: SessionTopicSource, lang?: string): string[] {
  const translations = session.translations ?? [];
  const requested = lang
    ? translations.find((item) => item.language?.code === lang)
    : undefined;
  const requestedTopics = normalizeTopics(requested?.topics);
  if (requestedTopics.length) return requestedTopics;

  const fallback =
    translations.find((item) => item.language?.code === DEFAULT_SESSION_LANGUAGE) ??
    translations[0];
  const fallbackTopics = normalizeTopics(fallback?.topics);
  if (fallbackTopics.length) return fallbackTopics;

  return normalizeTopics(session.topics);
}

function collectStoredTopics(session: SessionTopicSource): string[] {
  const fromTranslations = (session.translations ?? []).flatMap((translation) =>
    normalizeTopics(translation.topics)
  );
  return [...fromTranslations, ...normalizeTopics(session.topics)];
}

function withPublicSessionTopics<
  T extends SessionTopicSource & {
    translations: Array<{ language?: { code: string } | null }>;
  },
>(session: T, lang?: string) {
  return {
    ...session,
    topics: resolveSessionTopics(session, lang),
    translations: preferLangTranslations(session.translations, lang),
  };
}

/** Prefer translation for `lang`, else first available (avoids empty cards). */
function preferLangTranslations<
  T extends { language?: { code: string } | null },
>(translations: T[], lang?: string): T[] {
  if (!translations.length) return [];
  const match = lang
    ? translations.find((item) => item.language?.code === lang)
    : undefined;
  return [match ?? translations[0]!];
}

const sessionTranslationInclude = {
  include: { language: { select: { code: true } } },
} as const;

export const createSessionV2 = async ({
  eventId,
  createdById,
  startDate,
  endDate,
  translations,
  topics,
  speakerUserIds,
  limit,
}: {
  eventId: string;
  createdById: string;
  startDate: Date;
  endDate: Date;
  translations: SessionV2TranslationInput[];
  topics?: string[];
  speakerUserIds: string[];
  limit?: number;
}) => {
  if (endDate <= startDate) {
    throw new Error("End date must be after the start date.");
  }

  if (!speakerUserIds.length) {
    throw new Error(
      "Session requires at least one speaker linked to the track"
    );
  }

  const localizedTranslations = mergeLegacyTopics(
    await withLanguageCodes(translations),
    topics
  );

  return prisma.sessionV2.create({
    data: {
      eventId,
      startDate,
      endDate,
      topics: defaultLanguageTopics(localizedTranslations),
      speakerUserIds,
      limit: limit ?? null,
      createdById,
      translations: {
        createMany: {
          data: localizedTranslations.map(translationWriteData),
        },
      },
    },
    include: {
      translations: true,
    },
  });
};

export const updateSessionV2 = async ({
  sessionId,
  startDate,
  endDate,
  translations,
  topics,
  speakerUserIds,
  limit,
}: {
  sessionId: string;
  startDate: Date;
  endDate: Date;
  translations: SessionV2TranslationInput[];
  topics?: string[];
  speakerUserIds?: string[];
  limit?: number | null;
}) => {
  if (endDate <= startDate) {
    throw new Error("End date must be after the start date.");
  }

  if (speakerUserIds !== undefined && !speakerUserIds.length) {
    throw new Error(
      "Session requires at least one speaker linked to the track"
    );
  }

  await prisma.sessionV2Translation.deleteMany({
    where: { sessionId },
  });

  const localizedTranslations = mergeLegacyTopics(
    await withLanguageCodes(translations),
    topics
  );

  return prisma.sessionV2.update({
    where: { id: sessionId },
    data: {
      startDate,
      endDate,
      topics: defaultLanguageTopics(localizedTranslations),
      speakerUserIds: speakerUserIds ?? undefined,
      limit: limit ?? undefined,
      translations: {
        createMany: {
          data: localizedTranslations.map(translationWriteData),
        },
      },
    },
    include: {
      translations: true,
    },
  });
};

export const deleteSessionV2 = async (sessionId: string) => {
  return prisma.sessionV2.delete({
    where: { id: sessionId },
  });
};

export const getSessionV2ById = async (sessionId: string) => {
  return prisma.sessionV2.findUnique({
    where: { id: sessionId },
    include: {
      translations: true,
    },
  });
};

export const getPublicSessionV2Detail = async ({
  sessionId,
  eventId,
  lang = "en",
}: {
  sessionId: string;
  eventId: string;
  lang?: string;
}) => {
  const session = await prisma.sessionV2.findFirst({
    where: { id: sessionId, eventId },
    include: {
      translations: sessionTranslationInclude,
    },
  });

  if (!session) return null;

  const [enriched] = await enrichSessionsWithSpeakers([
    withPublicSessionTopics(session, lang),
  ]);
  return enriched;
};

export const hasSessionsV2ForEvent = async (eventId: string) => {
  const count = await prisma.sessionV2.count({
    where: { eventId },
  });
  return count > 0;
};

export const resolveSessionSpeakers = async (
  speakerUserIds: string[],
  eventId: string
) => {
  if (!speakerUserIds.length) return [];

  const users = await reportingPrisma.user.findMany({
    where: { id: { in: speakerUserIds } },
    select: {
      id: true,
      name: true,
      image: true,
      profiles: {
        where: { eventId },
        take: 1,
        select: {
          jobTitle: true,
          company: true,
          name: true,
          image: true,
        },
      },
    },
  });

  const userMap = new Map(users.map((u) => [u.id, u]));

  return speakerUserIds
    .map((id) => userMap.get(id))
    .filter(Boolean)
    .map((user) => {
      const profile = user!.profiles[0];
      return {
        id: user!.id,
        name: profile?.name || user!.name || "",
        image: profile?.image ?? user!.image ?? null,
        jobTitle: profile?.jobTitle || null,
        company: profile?.company || null,
      };
    });
};

export type ResolvedSessionSpeaker = Awaited<
  ReturnType<typeof resolveSessionSpeakers>
>[number];

export const enrichSessionsWithSpeakers = async <
  T extends { speakerUserIds: string[]; eventId: string },
>(
  sessions: T[]
) => {
  const allSpeakerIds = [
    ...new Set(sessions.flatMap((s) => s.speakerUserIds)),
  ];
  if (!allSpeakerIds.length) {
    return sessions.map((s) => ({ ...s, speakers: [] }));
  }

  const eventId = sessions[0]?.eventId;
  const speakers = await resolveSessionSpeakers(allSpeakerIds, eventId);
  const speakerMap = new Map(speakers.map((s) => [s.id, s]));

  return sessions.map((session) => ({
    ...session,
    speakers: session.speakerUserIds
      .map((id) => speakerMap.get(id))
      .filter((speaker): speaker is ResolvedSessionSpeaker => speaker != null),
  }));
};

export const enrichSessionsWithBookmarkAndAgendaState = async <
  T extends { id: string },
>(
  sessions: T[],
  eventId: string,
  userId: string
) => {
  if (!sessions.length) return sessions;

  const sessionIds = sessions.map((session) => session.id);
  const profile = await getProfileByEventIdAndUserId(eventId, userId);

  const [bookmarkedIds, agendaIds] = await Promise.all([
    getBookmarkedEntityIds(userId, eventId, EntityModel.SESSION, sessionIds),
    profile
      ? getProfileAgendaSessionIds(profile.id, sessionIds)
      : Promise.resolve(new Set<string>()),
  ]);

  return sessions.map((session) => ({
    ...session,
    isBookmarked: bookmarkedIds.has(session.id),
    isInAgenda: agendaIds.has(session.id),
  }));
};

export const getSessionsV2ByEventId = async (eventId: string) => {
  const sessions = await prisma.sessionV2.findMany({
    where: { eventId },
    include: {
      translations: sessionTranslationInclude,
    },
    orderBy: { startDate: "asc" },
  });

  return enrichSessionsWithSpeakers(sessions);
};

export const getPublicSessionsV2ByEventId = async (
  eventId: string,
  lang?: string
) => {
  const sessions = await prisma.sessionV2.findMany({
    where: { eventId },
    include: {
      translations: sessionTranslationInclude,
    },
    orderBy: { startDate: "asc" },
  });

  return enrichSessionsWithSpeakers(
    sessions.map((session) => withPublicSessionTopics(session, lang))
  );
};

export const getPublicSessionsV2BySpeakerUserId = async (
  eventId: string,
  speakerUserId: string,
  lang?: string,
  viewerUserId?: string
) => {
  const sessions = await prisma.sessionV2.findMany({
    where: {
      eventId,
      speakerUserIds: { has: speakerUserId },
    },
    include: {
      translations: sessionTranslationInclude,
    },
    orderBy: { startDate: "asc" },
  });

  const enrichedSessions = await enrichSessionsWithSpeakers(
    sessions.map((session) => withPublicSessionTopics(session, lang))
  );

  if (!viewerUserId) {
    return enrichedSessions;
  }

  return enrichSessionsWithBookmarkAndAgendaState(
    enrichedSessions,
    eventId,
    viewerUserId
  );
};

async function getSpeakerSessionIds(eventId: string, speakerUserId: string) {
  const sessions = await prisma.sessionV2.findMany({
    where: {
      eventId,
      speakerUserIds: { has: speakerUserId },
    },
    select: { id: true },
  });

  return sessions.map((session) => session.id);
}

export const getSpeakerSessionDocuments = async (
  eventId: string,
  speakerUserId: string,
  lang: string,
  userId?: string,
  bookmarkEventId?: string
) => {
  const sessionIds = await getSpeakerSessionIds(eventId, speakerUserId);
  if (!sessionIds.length) return [];

  const documents = await prisma.document.findMany({
    where: {
      entityId: { in: sessionIds },
      entityType: DocumentEntityType.SESSION,
    },
    include: {
      bookmarks: userId
        ? {
            where: {
              userId,
              eventId: bookmarkEventId,
            },
          }
        : false,
      translations: {
        where: { language: { code: lang } },
      },
    },
  });

  return documents.map((document) => ({
    ...document,
    name: document.translations?.[0]?.name,
    url: document.translations?.[0]?.url,
  }));
};

export const getSpeakerSessionVideos = async (
  eventId: string,
  speakerUserId: string,
  lang: string,
  userId?: string,
  bookmarkEventId?: string
) => {
  const sessionIds = await getSpeakerSessionIds(eventId, speakerUserId);
  if (!sessionIds.length) return [];

  const videos = await prisma.video.findMany({
    where: {
      entityId: { in: sessionIds },
      entityType: DocumentEntityType.SESSION,
    },
    include: {
      bookmarks: userId
        ? {
            where: {
              userId,
              eventId: bookmarkEventId,
            },
          }
        : false,
      translations: {
        where: { language: { code: lang } },
      },
    },
  });

  return videos.map((video) => ({
    ...video,
    name: video.translations?.[0]?.name,
    url: video.translations?.[0]?.url,
  }));
};

export type SessionV2ListFilters = {
  search?: string;
  locations?: string[];
  tracks?: string[];
  types?: string[];
  topics?: string[];
  day?: string;
  timezone?: string;
};

const DAY_WORDS = [
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
];

const SPEAKER_LOOKUP_CHUNK = 500;

/**
 * Speaker name search for the admin list path.
 *
 * Never scan the User collection. Collect speaker ids from this event's
 * sessions (indexed by eventId), then primary-key-read those users and
 * filter name / event profile name in process.
 */
async function resolveSpeakerUserIdsBySearch(
  eventId: string,
  search: string
): Promise<string[]> {
  const term = normalizeSessionSearchQuery(search);
  if (!term) return [];

  const sessions = await reportingPrisma.sessionV2.findMany({
    where: { eventId },
    select: { speakerUserIds: true },
  });
  const speakerIds = collectSpeakerUserIds(sessions);
  if (!speakerIds.length) return [];

  const needle = term.toLowerCase();
  const matched: string[] = [];

  for (let index = 0; index < speakerIds.length; index += SPEAKER_LOOKUP_CHUNK) {
    const chunk = speakerIds.slice(index, index + SPEAKER_LOOKUP_CHUNK);
    const users = await reportingPrisma.user.findMany({
      where: { id: { in: chunk } },
      select: {
        id: true,
        name: true,
        profiles: {
          where: { eventId },
          take: 1,
          select: { name: true },
        },
      },
    });
    for (const user of users) {
      const profileName = user.profiles[0]?.name;
      const values = [user.name, profileName];
      if (
        values.some(
          (value) =>
            typeof value === "string" && value.toLowerCase().includes(needle)
        )
      ) {
        matched.push(user.id);
      }
    }
  }

  return matched;
}

async function loadSpeakerSearchNames(
  eventId: string,
  speakerIds: string[]
): Promise<Map<string, Array<string | null | undefined>>> {
  const namesByUserId = new Map<string, Array<string | null | undefined>>();
  if (!speakerIds.length) return namesByUserId;

  for (let index = 0; index < speakerIds.length; index += SPEAKER_LOOKUP_CHUNK) {
    const chunk = speakerIds.slice(index, index + SPEAKER_LOOKUP_CHUNK);
    const users = await reportingPrisma.user.findMany({
      where: { id: { in: chunk } },
      select: {
        id: true,
        name: true,
        profiles: {
          where: { eventId },
          take: 1,
          select: { name: true },
        },
      },
    });
    for (const user of users) {
      namesByUserId.set(user.id, [user.profiles[0]?.name, user.name]);
    }
  }
  return namesByUserId;
}

function publicSessionBaseWhere(
  eventId: string,
  day?: string,
  timezone?: string
): Prisma.SessionV2WhereInput {
  const where: Prisma.SessionV2WhereInput = { eventId };
  if (day && timezone) {
    const { start, end } = getDayUtcRange(day, timezone);
    where.startDate = { gte: start, lte: end };
  }
  return where;
}

/**
 * Public text search for Agenda and Home Search Sessions.
 *
 * Reads through reportingPrisma on SessionV2(eventId[, startDate]) — never a
 * User collection scan or nested translation `$regex`. Name, description, and
 * speaker display names are filtered in process; facets apply on the same
 * rows. Only the page is speaker-enriched.
 */
async function listPublicSessionsV2BySearch({
  eventId,
  lang,
  timezone,
  page,
  limit,
  search,
  day,
  locations,
  tracks,
  types,
  topics,
}: {
  eventId: string;
  lang?: string;
  timezone: string;
  page: number;
  limit: number;
  search: string;
  day?: string;
  locations?: string[];
  tracks?: string[];
  types?: string[];
  topics?: string[];
}) {
  const pagination = normalizePublicSessionPagination(
    page,
    limit,
    PUBLIC_SESSIONS_V2_MAX_PAGE_SIZE
  );
  const where = publicSessionBaseWhere(eventId, day, timezone);

  const sessions = await reportingPrisma.sessionV2.findMany({
    where,
    select: {
      id: true,
      eventId: true,
      startDate: true,
      endDate: true,
      topics: true,
      speakerUserIds: true,
      limit: true,
      createdById: true,
      createdAt: true,
      updatedAt: true,
      translations: sessionTranslationInclude,
    },
    orderBy: { startDate: "asc" },
  });

  const speakerNames = await loadSpeakerSearchNames(
    eventId,
    collectSpeakerUserIds(sessions)
  );
  const matched = filterSessionsForPublicSearch(
    sessions as Array<(typeof sessions)[number] & SessionSearchRow>,
    search,
    speakerNames,
    {
      locations: trimFacetList(locations),
      tracks: trimFacetList(tracks),
      types: trimFacetList(types),
      topics: trimFacetList(topics),
    }
  );
  const window = pageSessionRows(matched, pagination.page, pagination.take);
  const enriched = await enrichSessionsWithSpeakers(
    window.pageRows.map((session) => withPublicSessionTopics(session, lang))
  );

  return {
    sessions: enriched,
    pagination: {
      page: pagination.page,
      pageSize: pagination.take,
      totalPages: window.totalPages,
      hasNextPage: window.hasNextPage,
      total: window.total,
    },
  };
}

function facetInFilter(
  stored: Array<string | null>,
  requested: string[]
) {
  const matches = expandFacetMatches(stored, requested);
  return { in: matches.length ? matches : requested };
}

async function buildSessionV2WhereClause(
  eventId: string,
  filters: SessionV2ListFilters = {}
): Promise<Prisma.SessionV2WhereInput> {
  const where: Prisma.SessionV2WhereInput = { eventId };
  const andConditions: Prisma.SessionV2WhereInput[] = [];

  const facetTranslationConditions: Prisma.SessionV2TranslationWhereInput[] =
    [];

  const needsFacetExpand =
    Boolean(filters.locations?.length) ||
    Boolean(filters.tracks?.length) ||
    Boolean(filters.types?.length) ||
    Boolean(filters.topics?.length);

  const storedLocations: Array<string | null> = [];
  const storedTracks: Array<string | null> = [];
  const storedTypes: Array<string | null> = [];
  const storedTopics: string[] = [];

  if (needsFacetExpand) {
    const sessions = await reportingPrisma.sessionV2.findMany({
      where: { eventId },
      select: {
        topics: true,
        translations: { select: { location: true, track: true, type: true, topics: true } },
      },
    });
    for (const session of sessions) {
      storedTopics.push(...collectStoredTopics(session));
      for (const translation of session.translations) {
        storedLocations.push(translation.location);
        storedTracks.push(translation.track);
        storedTypes.push(translation.type);
      }
    }
  }

  if (filters.locations?.length) {
    facetTranslationConditions.push({
      location: facetInFilter(storedLocations, filters.locations),
    });
  }

  if (filters.tracks?.length) {
    facetTranslationConditions.push({
      track: facetInFilter(storedTracks, filters.tracks),
    });
  }

  if (filters.types?.length) {
    facetTranslationConditions.push({
      type: facetInFilter(storedTypes, filters.types),
    });
  }

  if (facetTranslationConditions.length) {
    andConditions.push({
      translations: {
        some:
          facetTranslationConditions.length === 1
            ? facetTranslationConditions[0]
            : { AND: facetTranslationConditions },
      },
    });
  }

  const search = normalizeSessionSearchQuery(filters.search);
  if (search) {
    const matchingSpeakerIds = await resolveSpeakerUserIdsBySearch(
      eventId,
      search
    );

    const searchOrConditions: Prisma.SessionV2WhereInput[] = [
      {
        translations: {
          some: {
            OR: [
              { name: { contains: search, mode: "insensitive" } },
              { description: { contains: search, mode: "insensitive" } },
            ],
          },
        },
      },
    ];

    if (matchingSpeakerIds.length) {
      searchOrConditions.push({
        speakerUserIds: { hasSome: matchingSpeakerIds },
      });
    }

    andConditions.push({ OR: searchOrConditions });
  }

  if (filters.topics?.length) {
    const topics = expandFacetMatches(storedTopics, filters.topics);
    const topicValues = topics.length ? topics : filters.topics;
    andConditions.push({
      OR: [
        { translations: { some: { topics: { hasSome: topicValues } } } },
        { topics: { hasSome: topicValues } },
      ],
    });
  }

  if (filters.day && filters.timezone) {
    const { start, end } = getDayUtcRange(filters.day, filters.timezone);
    where.startDate = { gte: start, lte: end };
  }

  if (andConditions.length) {
    where.AND = andConditions;
  }

  return where;
}

function buildPaginationMeta(total: number, page: number, limit: number) {
  const totalPages = Math.ceil(total / limit) || 1;
  return {
    total,
    totalPages,
    currentPage: page,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1,
  };
}

export const listSessionsV2Paginated = async ({
  eventId,
  page = 1,
  limit = 10,
  timezone,
  ...filters
}: {
  eventId: string;
  page?: number;
  limit?: number;
  timezone?: string;
} & SessionV2ListFilters) => {
  const where = await buildSessionV2WhereClause(eventId, {
    ...filters,
    timezone,
  });
  const skip = (page - 1) * limit;

  const [total, sessions] = await Promise.all([
    prisma.sessionV2.count({ where }),
    prisma.sessionV2.findMany({
      where,
      include: { translations: sessionTranslationInclude },
      orderBy: { startDate: "asc" },
      skip,
      take: limit,
    }),
  ]);

  const enriched = await enrichSessionsWithSpeakers(sessions);

  return {
    sessions: enriched,
    pagination: buildPaginationMeta(total, page, limit),
  };
};

export const listPublicSessionsV2Paginated = async ({
  eventId,
  lang,
  timezone,
  page = 1,
  limit = 20,
  ...filters
}: {
  eventId: string;
  lang?: string;
  timezone: string;
  page?: number;
  limit?: number;
} & SessionV2ListFilters) => {
  const search = normalizeSessionSearchQuery(filters.search);
  if (search) {
    return listPublicSessionsV2BySearch({
      eventId,
      lang,
      timezone,
      page,
      limit,
      search,
      day: filters.day,
      locations: filters.locations,
      tracks: filters.tracks,
      types: filters.types,
      topics: filters.topics,
    });
  }

  const pagination = normalizePublicSessionPagination(
    page,
    limit,
    PUBLIC_SESSIONS_V2_LIST_MAX_PAGE_SIZE
  );
  const where = await buildSessionV2WhereClause(eventId, {
    ...filters,
    search: undefined,
    timezone,
  });
  const skip = (pagination.page - 1) * pagination.take;

  const [total, sessions] = await Promise.all([
    reportingPrisma.sessionV2.count({ where }),
    reportingPrisma.sessionV2.findMany({
      where,
      include: {
        translations: sessionTranslationInclude,
      },
      orderBy: { startDate: "asc" },
      skip,
      take: pagination.take,
    }),
  ]);

  const sessionsForLang = sessions.map((session) =>
    withPublicSessionTopics(session, lang)
  );

  const enriched = await enrichSessionsWithSpeakers(sessionsForLang);

  return {
    sessions: enriched,
    pagination: {
      page: pagination.page,
      pageSize: pagination.take,
      totalPages: Math.ceil(total / pagination.take) || 1,
      hasNextPage: pagination.page * pagination.take < total,
      total,
    },
  };
};

export const getPublicSessionV2Facets = async ({
  eventId,
  lang,
  timezone,
}: {
  eventId: string;
  lang?: string;
  timezone: string;
}) => {
  const sessions = await reportingPrisma.sessionV2.findMany({
    where: { eventId },
    include: {
      translations: sessionTranslationInclude,
    },
    orderBy: { startDate: "asc" },
  });

  const locations = new Set<string>();
  const tracks = new Set<string>();
  const types = new Set<string>();
  const topics = new Set<string>();
  const uniqueDays = new Map<string, string>();

  for (const session of sessions) {
    // Facets must only use the requested language — no fallback (avoids EN chips in AR UI)
    const translation = lang
      ? session.translations.find((item) => item.language?.code === lang)
      : session.translations[0];
    const location = trimFacetValue(translation?.location);
    const track = trimFacetValue(translation?.track);
    const type = trimFacetValue(translation?.type);
    if (location) locations.add(location);
    if (track) tracks.add(track);
    if (type) types.add(type);
    for (const topic of translation?.topics ?? []) {
      const trimmedTopic = trimFacetValue(topic);
      if (trimmedTopic) topics.add(trimmedTopic);
    }

    const zoned = moment(session.startDate).tz(timezone);
    const key = zoned.format("YYYY-MM-DD");
    if (!uniqueDays.has(key)) {
      const word = DAY_WORDS[uniqueDays.size] ?? String(uniqueDays.size + 1);
      uniqueDays.set(key, `Day ${word}`);
    }
  }

  return {
    locations: [...locations].sort(),
    tracks: [...tracks].sort(),
    types: [...types].sort(),
    topics: [...topics].sort(),
    days: Array.from(uniqueDays.entries()).map(([key, label]) => ({
      key,
      label,
    })),
  };
};

export const getSessionV2Facets = async ({
  eventId,
}: {
  eventId: string;
}) => {
  const sessions = await prisma.sessionV2.findMany({
    where: { eventId },
    include: { translations: true },
    orderBy: { startDate: "asc" },
  });

  const locations = new Set<string>();
  const tracks = new Set<string>();
  const types = new Set<string>();
  const topics = new Set<string>();

  for (const session of sessions) {
    for (const translation of session.translations) {
      const location = trimFacetValue(translation.location);
      const track = trimFacetValue(translation.track);
      const type = trimFacetValue(translation.type);
      if (location) locations.add(location);
      if (track) tracks.add(track);
      if (type) types.add(type);
    }
    for (const topic of collectStoredTopics(session)) {
      const trimmedTopic = trimFacetValue(topic);
      if (trimmedTopic) topics.add(trimmedTopic);
    }
  }

  return {
    locations: [...locations].sort(),
    tracks: [...tracks].sort(),
    types: [...types].sort(),
    topics: [...topics].sort(),
  };
};

export const bulkDeleteSessionsV2 = async ({
  eventId,
  sessionIds,
}: {
  eventId: string;
  sessionIds: string[];
}) => {
  if (!sessionIds.length) {
    throw new Error("No sessions selected");
  }

  const existing = await prisma.sessionV2.findMany({
    where: { eventId, id: { in: sessionIds } },
    select: { id: true },
  });

  if (existing.length !== sessionIds.length) {
    throw new Error("One or more sessions do not belong to this event");
  }

  return prisma.sessionV2.deleteMany({
    where: { eventId, id: { in: sessionIds } },
  });
};

export type SessionSpeakerWithEmail = ResolvedSessionSpeaker & {
  email: string;
};

export const resolveSessionSpeakersWithEmail = async (
  speakerUserIds: string[],
  eventId: string
): Promise<SessionSpeakerWithEmail[]> => {
  if (!speakerUserIds.length) return [];

  const users = await prisma.user.findMany({
    where: { id: { in: speakerUserIds } },
    select: {
      id: true,
      name: true,
      email: true,
      image: true,
      profiles: {
        where: { eventId },
        take: 1,
        select: {
          jobTitle: true,
          company: true,
          name: true,
          image: true,
          email: true,
        },
      },
    },
  });

  const userMap = new Map(users.map((u) => [u.id, u]));

  return speakerUserIds
    .map((id) => userMap.get(id))
    .filter(Boolean)
    .map((user) => {
      const profile = user!.profiles[0];
      return {
        id: user!.id,
        name: profile?.name || user!.name || "",
        email: profile?.email || user!.email || "",
        image: profile?.image ?? user!.image ?? null,
        jobTitle: profile?.jobTitle || null,
        company: profile?.company || null,
      };
    });
};

export const enrichSessionsWithSpeakersAndEmail = async <
  T extends { speakerUserIds: string[]; eventId: string },
>(
  sessions: T[]
) => {
  const allSpeakerIds = [
    ...new Set(sessions.flatMap((s) => s.speakerUserIds)),
  ];
  if (!allSpeakerIds.length) {
    return sessions.map((s) => ({ ...s, speakers: [] }));
  }

  const eventId = sessions[0]?.eventId;
  const speakers = await resolveSessionSpeakersWithEmail(
    allSpeakerIds,
    eventId
  );
  const speakerMap = new Map(speakers.map((s) => [s.id, s]));

  return sessions.map((session) => ({
    ...session,
    speakers: session.speakerUserIds
      .map((id) => speakerMap.get(id))
      .filter(
        (speaker): speaker is SessionSpeakerWithEmail => speaker != null
      ),
  }));
};

export const getSessionsV2ForExport = async (eventId: string) => {
  const sessions = await prisma.sessionV2.findMany({
    where: { eventId },
    include: {
      translations: {
        include: {
          language: { select: { code: true } },
        },
      },
    },
    orderBy: { startDate: "asc" },
  });

  return enrichSessionsWithSpeakersAndEmail(sessions);
};

export {
  buildSessionCsvExportContent,
  buildSessionCsvSampleContent,
  buildSessionCsvTemplate,
  type SessionCsvTemplate,
} from "./csv-template";

export const buildSessionsV2CsvExport = async (eventId: string) => {
  const event = await getEvent(eventId);
  if (!event) {
    throw new Error("Event not found");
  }

  const languages = await prisma.language.findMany({
    where: { id: { in: event.languagesIds } },
    select: { code: true, name: true },
  });

  if (!languages.length) {
    throw new Error("Event has no languages configured");
  }

  const sessions = await getSessionsV2ForExport(eventId);
  const template = buildSessionCsvTemplate(languages);
  const csv = buildSessionCsvExportContent(
    sessions,
    template,
    event.timezone
  );

  return {
    csv,
    filename: `sessions-${event.slug}.csv`,
  };
};

export type BulkImportSessionRow = Record<string, string | number | undefined>;

export type BulkImportSessionsResult = {
  created: number;
  failed: { row: number; error: string }[];
};

function parseCsvList(value?: string | number): string[] {
  if (value === undefined || value === null) return [];
  const str = String(value).trim();
  if (!str) return [];
  return str.split(",").map((item) => item.trim()).filter(Boolean);
}

function combineSessionDateAndTime(
  date: string,
  time: string,
  timezone: string
): Date {
  const zoned = moment.tz(`${date} ${time}`, "YYYY-MM-DD HH:mm", timezone);
  if (!zoned.isValid()) {
    throw new Error(`Invalid date/time: ${date} ${time}`);
  }
  return zoned.toDate();
}

function applySessionTranslationFallback(
  translations: SessionV2TranslationInput[]
): SessionV2TranslationInput[] {
  const fallback = translations.find((trans) => trans.name?.trim());
  if (!fallback) return translations;

  return translations.map((trans) => ({
    languageId: trans.languageId,
    languageCode: trans.languageCode,
    name: trans.name?.trim() || fallback.name,
    description: trans.description?.trim() || fallback.description || undefined,
    location: trans.location?.trim() || fallback.location || undefined,
    track: trans.track?.trim() || fallback.track || undefined,
    type: trans.type?.trim() || fallback.type || undefined,
    topics: normalizeTopics(trans.topics),
  }));
}

export const resolveSpeakerUserIdForGroup = async (
  email: string,
  name: string | undefined,
  participantGroupId: string,
  eventId: string,
  whiteLabelId: string,
  sessionUser: User
): Promise<string> => {
  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail) {
    throw new Error("Speaker email is required");
  }

  const profile = await prisma.profile.findFirst({
    where: {
      eventId,
      participantGroupIds: { has: participantGroupId },
      userId: { not: null },
      OR: [
        { email: { equals: normalizedEmail, mode: "insensitive" } },
        {
          user: {
            email: { equals: normalizedEmail, mode: "insensitive" },
          },
        },
      ],
    },
    select: { userId: true },
  });

  if (profile?.userId) {
    return profile.userId;
  }

  const speakerName =
    name?.trim() || normalizedEmail.split("@")[0] || "Speaker";

  const results = await addParticipantsToParticipantGroup(
    participantGroupId,
    eventId,
    [
      {
        name: speakerName,
        email: normalizedEmail,
      } as Profile,
    ],
    ParticipantGroupType.SPEAKERS,
    sessionUser,
    whiteLabelId
  );

  const created = await Promise.all(results);
  const userId = (created[0] as { id?: string } | undefined)?.id;

  if (!userId) {
    throw new Error(`Failed to resolve speaker: ${normalizedEmail}`);
  }

  return userId;
};

export const bulkImportSessionsV2 = async ({
  eventId,
  createdById,
  participantGroupId,
  eventTimezone,
  rows,
  sessionUser,
}: {
  eventId: string;
  createdById: string;
  participantGroupId: string;
  eventTimezone: string;
  rows: BulkImportSessionRow[];
  sessionUser: User;
}): Promise<BulkImportSessionsResult> => {
  if (!rows.length) {
    throw new Error("No sessions to import");
  }

  const [speakerGroups, event] = await Promise.all([
    getParticipantGroupsByEventIdAndType(eventId, ParticipantGroupType.SPEAKERS),
    getEvent(eventId),
  ]);

  const group = speakerGroups.find((g) => g.id === participantGroupId);
  if (!group) {
    throw new Error("Invalid speaker participant group");
  }

  if (!event?.whiteLabelId) {
    throw new Error("Event not found");
  }

  const languages = await prisma.language.findMany({
    where: {
      id: {
        in: event.languagesIds,
      },
    },
  });
  if (!languages.length) {
    throw new Error("Event has no languages configured");
  }

  const speakerNameByEmail = new Map<string, string>();
  for (const row of rows) {
    const speakerEmails = parseCsvList(row.speakerEmails);
    const speakerNames = parseCsvList(row.speakerNames);

    speakerEmails.forEach((email, speakerIndex) => {
      const cacheKey = email.toLowerCase();
      if (!speakerNameByEmail.has(cacheKey)) {
        speakerNameByEmail.set(cacheKey, speakerNames[speakerIndex] ?? "");
      }
    });
  }

  const speakerCache = new Map<string, string>();
  const speakerResolutionResults = await Promise.allSettled(
    [...speakerNameByEmail.entries()].map(async ([email, name]) => {
      const userId = await resolveSpeakerUserIdForGroup(
        email,
        name,
        participantGroupId,
        eventId,
        event.whiteLabelId,
        sessionUser
      );
      return { email, userId };
    })
  );

  for (const resolution of speakerResolutionResults) {
    if (resolution.status === "fulfilled") {
      speakerCache.set(resolution.value.email, resolution.value.userId);
    }
  }

  const rowResults = await Promise.all(
    rows.map(async (row, index) => {
      const rowNumber = index + 1;

      try {
        const date = String(row.date ?? "").trim();
        const startTime = String(row.startTime ?? "").trim();
        const endTime = String(row.endTime ?? "").trim();

        if (!date || !startTime || !endTime) {
          throw new Error("Date, start time, and end time are required");
        }

        const speakerEmails = parseCsvList(row.speakerEmails);
        if (!speakerEmails.length) {
          throw new Error("At least one speaker email is required");
        }

        const speakerUserIds = speakerEmails.map((email) => {
          const userId = speakerCache.get(email.toLowerCase());
          if (!userId) {
            throw new Error(`Failed to resolve speaker: ${email}`);
          }
          return userId;
        });

        const rawTranslations: SessionV2TranslationInput[] = languages.map(
          (language) => ({
            languageId: language.id,
            languageCode: language.code,
            name: String(row[`name_${language.code}`] ?? "").trim(),
            description: String(row[`description_${language.code}`] ?? "").trim(),
            location: String(row[`location_${language.code}`] ?? "").trim(),
            track: String(row[`track_${language.code}`] ?? "").trim(),
            type: String(row[`type_${language.code}`] ?? "").trim(),
            topics: (() => {
              const localized = parseCsvList(row[`topics_${language.code}`]);
              if (localized.length) return localized;
              if (language.code === DEFAULT_SESSION_LANGUAGE) {
                return parseCsvList(row.topics);
              }
              return [];
            })(),
          })
        );

        const translations = applySessionTranslationFallback(
          rawTranslations
        ).filter((translation) => translation.name?.trim());

        if (!translations.length) {
          throw new Error("Session name is required in at least one language");
        }

        const startDate = combineSessionDateAndTime(
          date,
          startTime,
          eventTimezone
        );
        const endDate = combineSessionDateAndTime(date, endTime, eventTimezone);

        if (endDate <= startDate) {
          throw new Error("End time must be after start time");
        }

        const attendeeLimitRaw = row.attendeeLimit;
        const limit =
          attendeeLimitRaw !== undefined && attendeeLimitRaw !== ""
            ? Number(attendeeLimitRaw)
            : undefined;

        await createSessionV2({
          eventId,
          createdById,
          startDate,
          endDate,
          translations,
          topics: parseCsvList(row.topics),
          speakerUserIds,
          limit: Number.isFinite(limit) ? limit : undefined,
        });

        return { ok: true as const };
      } catch (error) {
        return {
          ok: false as const,
          row: rowNumber,
          error: error instanceof Error ? error.message : "Import failed",
        };
      }
    })
  );

  const result: BulkImportSessionsResult = { created: 0, failed: [] };

  for (const rowResult of rowResults) {
    if (rowResult.ok) {
      result.created += 1;
    } else {
      result.failed.push({
        row: rowResult.row,
        error: rowResult.error,
      });
    }
  }

  return result;
};
